/**
 * HowToFixSection Component
 * 
 * Expandable section showing actionable fix instructions for each issue type.
 * Part of Phase 0: Enhanced Issue Cards
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Mail,
  Clock,
  Phone,
  FileText,
  ExternalLink,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import type { IssueCard } from "@/types/lcopilot";

interface HowToFixSectionProps {
  issue: IssueCard;
  lcNumber?: string;
  companyName?: string;
  onDraftEmail?: (context: EmailDraftContext) => void;
}

export interface EmailDraftContext {
  issueTitle: string;
  issueType: string;
  recipient: string;
  subject: string;
  context: string;
}

// Fix instruction templates based on issue patterns
interface FixInstruction {
  copyText?: string;
  steps: string[];
  timeEstimate: string;
  contactInfo?: {
    name: string;
    role: string;
    phone?: string;
    email?: string;
  };
  needsEmail?: boolean;
  emailRecipient?: string;
  docGeneratorLink?: string;
  isInternal?: boolean; // Can be fixed without external parties
}

const getWorkflowLane = (issue: IssueCard): string => {
  const bucket = (issue as any).bucket;
  if ((issue as any).workflow_lane) {
    return String((issue as any).workflow_lane);
  }
  if (bucket === "Compliance / Risk Review") {
    return "compliance_review";
  }
  if (bucket === "Extraction / Manual Review") {
    return "manual_review";
  }
  return "documentary_review";
};

// Get fix instructions based on issue type/rule
function getFixInstructions(issue: IssueCard, lcNumber?: string): FixInstruction | null {
  const rule = issue.rule?.toUpperCase() || "";
  const title = issue.title?.toLowerCase() || "";
  const expected = issue.expected || "";
  const workflowLane = getWorkflowLane(issue);
  const requirementKind = String((issue as any).requirement_kind ?? '').toLowerCase();
  const requirementSource = String((issue as any).requirement_source ?? '').toLowerCase();
  const requirementText = String((issue as any).requirement_text ?? '').trim();
  const requirementDoc =
    issue.documentType ||
    issue.documentName ||
    (Array.isArray(issue.documents)
      ? issue.documents.find((name) => String(name ?? '').trim().toLowerCase() !== 'letter_of_credit')
      : '') ||
    'document';

  if (requirementKind === 'document_exact_wording' && requirementSource === 'requirements_graph_v1') {
    return {
      copyText: requirementText || undefined,
      steps: [
        `Update the ${requirementDoc} to include the exact LC-required statement.`,
        requirementText
          ? 'Use the exact wording shown above without paraphrasing, abbreviation, or layout changes that alter meaning.'
          : 'Use the LC-required wording exactly as issued without paraphrasing or abbreviation.',
        'Reissue, sign, stamp, or certify the document as required before presentation.',
        'If the document cannot be changed, seek an LC amendment before presentation.',
      ],
      timeEstimate: '30-60 minutes if you control the document | 1-3 business days if a third party must reissue it',
      isInternal: !/bill of lading|insurance|inspection|carrier|certificate of origin/i.test(String(requirementDoc)),
      docGeneratorLink:
        String(requirementDoc).toLowerCase().includes('beneficiary')
          ? "/hub/doc-generator?type=beneficiary-certificate"
          : undefined,
    };
  }

  if (workflowLane === "compliance_review") {
    return {
      steps: [
        "Confirm the trigger, screening context, and supporting evidence.",
        "Record the review rationale, including any false-positive analysis.",
        "Route the case to internal compliance for disposition and approval.",
        "Keep submission on hold until the compliance decision is documented.",
      ],
      timeEstimate: "Same-day internal review unless external escalation is required",
      isInternal: true,
    };
  }
  
  // BIN/TIN Missing
  if (rule.includes("BIN") || title.includes("bin missing")) {
    const binMatch = expected.match(/BIN[:\s]*['"]?([0-9-]+)['"]?/i);
    const binNumber = binMatch?.[1] || "[BIN from LC]";
    return {
      copyText: `Exporter BIN: ${binNumber}`,
      steps: [
        "Add the BIN number to all listed documents",
        "Place it in the header or footer area",
        "For B/L, email your carrier/freight forwarder",
      ],
      timeEstimate: "1-2 hours (docs you control) | 2-3 days (B/L)",
      needsEmail: true,
      emailRecipient: "carrier",
      isInternal: false,
    };
  }
  
  // TIN Missing
  if (rule.includes("TIN") || title.includes("tin missing")) {
    const tinMatch = expected.match(/TIN[:\s]*['"]?([0-9-]+)['"]?/i);
    const tinNumber = tinMatch?.[1] || "[TIN from LC]";
    return {
      copyText: `Exporter TIN: ${tinNumber}`,
      steps: [
        "Add the TIN number to all listed documents",
        "Place it near the BIN number if both required",
        "For B/L, include in carrier amendment request",
      ],
      timeEstimate: "1-2 hours (docs you control) | 2-3 days (B/L)",
      needsEmail: true,
      emailRecipient: "carrier",
      isInternal: false,
    };
  }
  
  // Missing Inspection Certificate
  if (rule.includes("INSPECTION") || title.includes("inspection certificate")) {
    return {
      steps: [
        "Contact SGS or Intertek to schedule pre-shipment inspection",
        "Prepare goods for inspection at your warehouse",
        "Inspection typically takes 1 day",
        "Certificate issued within 2-3 days after inspection",
      ],
      timeEstimate: "3-5 business days",
      contactInfo: {
        name: "SGS Bangladesh / Intertek",
        role: "Pre-Shipment Inspection",
        phone: "+880-2-9881234",
        email: "bd.certification@sgs.com",
      },
      needsEmail: true,
      emailRecipient: "inspector",
      isInternal: false,
    };
  }
  
  // Missing Beneficiary Certificate
  if (rule.includes("BENEFICIARY") || title.includes("beneficiary certificate")) {
    return {
      steps: [
        "Create a certificate on your company letterhead",
        "Include the required statement from LC clause 46A",
        "Sign and stamp with company seal",
        "Date should be before B/L date",
      ],
      timeEstimate: "30 minutes",
      isInternal: true,
      docGeneratorLink: "/hub/doc-generator?type=beneficiary-certificate",
    };
  }
  
  // B/L Missing Fields (Voyage, Weight, etc.)
  if (rule.includes("BL-") || rule.includes("AI-BL-") || title.includes("b/l missing")) {
    const missingField = title.replace(/b\/l missing required field:\s*/i, "").trim();
    return {
      steps: [
        `Request amended B/L from your shipping line/carrier`,
        `Specify the missing field: ${missingField}`,
        "Include your B/L number and LC number in request",
        "Ask for expedited processing if LC deadline is near",
      ],
      timeEstimate: "1-3 business days",
      needsEmail: true,
      emailRecipient: "carrier",
      isInternal: false,
    };
  }
  
  // Packing List Issues
  if (rule.includes("PL-") || rule.includes("AI-PL-") || title.includes("packing list")) {
    return {
      steps: [
        "Update your packing list with the required information",
        "Ensure carton-wise breakdown matches LC requirements",
        "Include all required fields: size, weight, dimensions",
        "Reprint and re-stamp if already issued",
      ],
      timeEstimate: "1-2 hours",
      isInternal: true,
    };
  }
  
  // Invoice Issues
  if (rule.includes("INV-") || rule.includes("AI-INV-") || title.includes("invoice")) {
    return {
      steps: [
        "Update your commercial invoice with the required information",
        "Ensure amounts match LC exactly (check unit price × quantity)",
        "Verify all mandatory fields are present",
        "Reprint and re-stamp",
      ],
      timeEstimate: "1-2 hours",
      isInternal: true,
    };
  }
  
  // Certificate of Origin Issues
  if (rule.includes("COO-") || title.includes("certificate of origin")) {
    return {
      steps: [
        "Prepare corrected C/O with all required information",
        "Submit to EPB/Chamber of Commerce for certification",
        "Ensure BIN/TIN and other required fields are included",
      ],
      timeEstimate: "1-2 business days",
      contactInfo: {
        name: "Export Promotion Bureau / Chamber",
        role: "C/O Certification",
      },
      isInternal: false,
    };
  }
  
  // Sanctions/Vessel Issues
  if (rule.includes("SANCTIONS") || title.includes("sanctions") || title.includes("vessel")) {
    return {
      steps: [
        "Review the sanctions match details carefully",
        "If false positive: Document your due diligence",
        "If genuine concern: Contact your bank's trade compliance",
        "Consider requesting a different vessel from carrier",
      ],
      timeEstimate: "Varies - consult compliance team",
      isInternal: false,
    };
  }
  
  // Amount/Date Mismatch
  if (title.includes("amount") || title.includes("mismatch") || title.includes("discrepancy")) {
    return {
      steps: [
        "Compare your document against LC terms exactly",
        "Correct the value to match LC (or request LC amendment)",
        "Ensure calculations are accurate (unit price × quantity)",
        "Check for rounding differences",
      ],
      timeEstimate: "1-2 hours (document fix) | 3-5 days (LC amendment)",
      isInternal: true,
    };
  }
  
  // Generic fallback
  return {
    steps: [
      "Review the Expected vs Found values above",
      "Correct your document to match LC requirements",
      "If document cannot be corrected, consider LC amendment",
    ],
    timeEstimate: "Varies by document type",
    isInternal: true,
  };
}

export function HowToFixSection({ 
  issue, 
  lcNumber, 
  companyName,
  onDraftEmail 
}: HowToFixSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  
  const instructions = getFixInstructions(issue, lcNumber);
  const workflowLane = getWorkflowLane(issue);
  const sectionTitle = workflowLane === "documentary_review" ? "How to Fix" : "Review Workflow";
  const internalBadgeLabel = workflowLane === "compliance_review" ? "Internal review" : "Self-fixable";
  const stepsLabel = workflowLane === "compliance_review" ? "Review steps:" : "Steps:";
  
  if (!instructions) return null;
  
  const handleCopy = async () => {
    if (!instructions.copyText) return;
    
    try {
      await navigator.clipboard.writeText(instructions.copyText);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Text copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Please select and copy manually",
        variant: "destructive",
      });
    }
  };
  
  const handleDraftEmail = () => {
    if (onDraftEmail) {
      const recipientMap: Record<string, string> = {
        carrier: "Shipping Line / Freight Forwarder",
        inspector: "SGS/Intertek Inspection",
        bank: "Issuing Bank",
        chamber: "Chamber of Commerce",
      };
      
      onDraftEmail({
        issueTitle: issue.title || "Document Issue",
        issueType: issue.rule || "general",
        recipient: recipientMap[instructions.emailRecipient || "carrier"] || "External Party",
        subject: `Document Amendment Request - LC ${lcNumber || "[LC Number]"}`,
        context: issue.description || "",
      });
    }
  };
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-3">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "w-full justify-between text-sm font-medium",
            "hover:bg-[#B2F273]/10 hover:text-[#00261C]",
            isOpen && "bg-[#B2F273]/10 text-[#00261C]"
          )}
        >
          <span className="flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            {sectionTitle}
            {instructions.isInternal && (
              <Badge variant="outline" className="text-[10px] ml-1 bg-[#B2F273]/10 text-[#00261C] border-[#B2F273]/40">
                {internalBadgeLabel}
              </Badge>
            )}
          </span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-2">
        <div className="p-4 bg-gradient-to-br from-[#B2F273]/5 to-[#B2F273]/10 rounded-lg border border-[#B2F273]/30 space-y-4">

          {/* Copy-paste text block */}
          {instructions.copyText && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-[#00382E] dark:text-[#B2F273] uppercase tracking-wide">
                📋 Add this to your documents:
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-card rounded border border-[#B2F273]/30 text-sm font-mono">
                  {instructions.copyText}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 border-[#B2F273]/30 hover:bg-[#B2F273]/10"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-[#00382E]" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Steps */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-[#00382E] dark:text-[#B2F273] uppercase tracking-wide">
              {stepsLabel}
            </p>
            <ol className="space-y-1.5 text-sm">
              {instructions.steps.map((step, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#B2F273]/20 text-[#00261C] dark:text-[#B2F273] text-xs flex items-center justify-center font-medium">
                    {idx + 1}
                  </span>
                  <span className="text-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Time estimate */}
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-[#00382E] dark:text-[#B2F273]" />
            <span className="text-muted-foreground">
              Estimated time: <span className="font-medium text-foreground">{instructions.timeEstimate}</span>
            </span>
          </div>

          {/* Contact info */}
          {instructions.contactInfo && (
            <div className="p-3 bg-card rounded-lg border border-border">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Contact
              </p>
              <p className="font-medium text-foreground">
                {instructions.contactInfo.name}
              </p>
              <p className="text-sm text-muted-foreground">
                {instructions.contactInfo.role}
              </p>
              {instructions.contactInfo.phone && (
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <Phone className="w-3 h-3" />
                  {instructions.contactInfo.phone}
                </p>
              )}
              {instructions.contactInfo.email && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {instructions.contactInfo.email}
                </p>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            {instructions.needsEmail && onDraftEmail && (
              <Button
                size="sm"
                variant="outline"
                className="border-[#B2F273]/40 text-[#00382E] hover:bg-[#B2F273]/10 hover:text-[#00261C]"
                onClick={handleDraftEmail}
              >
                <Mail className="w-4 h-4 mr-1" />
                Draft Email with AI
              </Button>
            )}

            {instructions.docGeneratorLink && (
              <Button
                size="sm"
                variant="outline"
                className="border-border hover:bg-muted"
                asChild
              >
                <a href={instructions.docGeneratorLink} target="_blank" rel="noopener noreferrer">
                  <FileText className="w-4 h-4 mr-1" />
                  Generate Document
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default HowToFixSection;

